import { useCallback, useRef, useState } from "react";
import { NotConnectedError, describeError } from "../api/errors";
import type { OperationId } from "../api/operations";
import { announce } from "../state/announcer";
import { useServices } from "./services";

export type ActionResult<R> = { ok: true; value: R } | { ok: false; cause: unknown };

export interface ActionState {
  connected: boolean;
  running: boolean;
  /** 失敗したときの説明（成功したら null） */
  error: string | null;
  /** 最後の成功の時刻（同じ画面で「保存しました」を出すため） */
  doneAt: number | null;
  /** 失敗の元（コードで分けたいとき） */
  cause: unknown;
}

/**
 * 保存・投稿などの変更を一つ実行する。契約に無い操作は呼ばない（未接続と出す）。
 * 成功の文はサーバーの応答が返ってから出す。モックのときは「（モック）」を付ける
 */
export function useAction<A extends unknown[], R>(op: OperationId, fn: (...args: A) => Promise<R>) {
  const { api } = useServices();
  const connected = api.isConnected(op);
  const [state, setState] = useState<ActionState>({ connected, running: false, error: null, doneAt: null, cause: null });
  const running = useRef(false);

  /** 失敗しても投げない（ok: false を返し、error に説明が入る） */
  const run = useCallback(
    async (...args: A): Promise<ActionResult<R>> => {
      if (running.current) return { ok: false, cause: null };
      if (!api.isConnected(op)) {
        const msg = new NotConnectedError(op).message;
        setState({ connected: false, running: false, error: msg, doneAt: null, cause: null });
        announce(msg, "assertive");
        return { ok: false, cause: new NotConnectedError(op) };
      }
      running.current = true;
      setState((s) => ({ ...s, running: true, error: null }));
      try {
        const result = await fn(...args);
        setState({ connected: true, running: false, error: null, doneAt: Date.now(), cause: null });
        return { ok: true, value: result };
      } catch (e) {
        const msg = describeError(e);
        setState({ connected: true, running: false, error: msg, doneAt: null, cause: e });
        announce(msg, "assertive");
        return { ok: false, cause: e };
      } finally {
        running.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, op, fn],
  );

  const suffix = api.mode === "mock" ? "（モック）" : "";
  return { ...state, connected, run, suffix };
}
